"use client";

import React from "react";
import { CircleCheck, CircleX, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const comparisonData = [{
  feature: "Comfort & Fit",
  cpap: "Bulky mask & straps",
  oralAppliance: "Custom dental fit",
  cpapGood: false
}, {
  feature: "Portability",
  cpap: "Requires separate bag",
  oralAppliance: "Fits in your pocket",
  cpapGood: false
}, {
  feature: "Noise Level",
  cpap: "Machine humming",
  oralAppliance: "Completely silent",
  cpapGood: false
}, {
  feature: "Power Source",
  cpap: "Needs electricity to run",
  oralAppliance: "No power needed",
  cpapGood: false
}, {
  feature: "Maintenance",
  cpap: "Daily hose & mask cleaning",
  oralAppliance: "Simple daily brushing",
  cpapGood: false
}];
export default function WhyChooseUs() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="why-choose-us" className="bg-background py-20 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-6 font-sans">
            The LA Difference: Oral Appliance vs. CPAP
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Discover why busy Los Angeles professionals are making the switch to comfortable, 
            custom-fitted oral appliances for effective, CPAP-free sleep apnea relief.
          </p>
        </div>

        {}
        <div ref={contentRef} className={`grid md:grid-cols-2 gap-12 lg:gap-16 items-center transition-all duration-700 delay-200 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          
          {}
          <div className="order-2 md:order-1 flex flex-col gap-8">
            <Card className="overflow-hidden shadow-lg border-border/50">
              <Table>
                <TableHeader className="bg-muted/30">
                  <TableRow>
                    <TableHead className="w-[30%] font-semibold text-secondary py-4">Feature</TableHead>
                    <TableHead className="w-[35%] font-semibold text-secondary py-4">Traditional CPAP</TableHead>
                    <TableHead className="w-[35%] font-semibold text-primary py-4 bg-primary/5">Oral Appliance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((item, i) => <TableRow key={i} data-index={i} className="group">
                      <TableCell className="font-medium text-secondary py-4">
                        {item.feature}
                      </TableCell>
                      <TableCell className="text-muted-foreground py-4">
                        <div className="flex items-center gap-2">
                          <CircleX className="size-4 text-muted-foreground/50 shrink-0" />
                          <span className="text-sm">{item.cpap}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4 bg-primary/5 group-hover:bg-primary/10 transition-colors">
                        <div className="flex items-center gap-2">
                          <CircleCheck className="size-4 text-primary shrink-0" />
                          <span className="text-sm font-medium text-secondary">{item.oralAppliance}</span>
                        </div>
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </Card>

            <div className="flex flex-col sm:flex-row gap-4 items-center justify-start">
              <Button asChild size="lg" className="w-full sm:w-auto rounded-full px-8">
                <Link to="/ContactPage">
                  Book Free Consultation
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
              <p className="text-sm text-muted-foreground font-medium">
                Covered by most major insurances
              </p>
            </div>
          </div>

          {}
          <div className="order-1 md:order-2 relative">
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-accent/20 rounded-3xl transform translate-x-4 translate-y-4 -z-10"></div>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1621270/img/modern_oral_appliance_render.png" alt="Detailed 3D render of a modern, custom-fitted oral appliance for sleep apnea" className="w-full h-auto object-cover rounded-3xl shadow-xl border border-border/50" />
            
            {}
            <div className="absolute -bottom-6 -left-6 bg-card p-4 rounded-2xl shadow-lg border border-border/50 flex items-center gap-4 max-w-[240px]">
              <div className="bg-primary/10 p-3 rounded-full shrink-0">
                <CircleCheck className="size-6 text-primary" />
              </div>
              <div>
                <p className="text-sm font-bold text-secondary leading-tight">FDA Cleared</p>
                <p className="text-xs text-muted-foreground mt-0.5">Safe & Effective</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}